import React, { useState } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { placeNewOrder } from "../redux/actions/placeOrderAction";
import { toast } from "react-toastify";
import * as Alert from "../components/common/Alert";
import { addAddress, getAddressList } from "../redux/actions/addressActions";
import { handlePayment } from "../Common/handlePayment";
import { addPaymentResponse } from "../Services/MainServices";
import * as other from "../components/other/";
import { fetchCoupons } from "../redux/actions/couponsActions";
import { fetchCart } from "../redux/actions/cartActions";
import { featchShippingCharges } from "../redux/actions/shipingAction";
import axios from "axios";
import { Search } from "lucide-react";

const addressForm = [
  { label: "Full Name*", key: "full_name", value: "", enable: false },
  { label: "Mobile No*", key: "mobile_number", value: "", enable: false },
  { label: "PIN Code*", key: "pin_code", value: "", enable: false },
  { label: "House No*", key: "house_no", value: "", enable: false },
  { label: "Street*", key: "street", value: "", enable: false },
  { label: "Landmark", key: "landmark", value: "", enable: false },
  { label: "City*", key: "city", value: "", enable: false },
  { label: "State*", key: "state", value: "", enable: false },
  { label: "Country*", key: "country", value: "", enable: false },
  { label: "Address Type*", key: "address_type", value: "HOME", enable: false },
];

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [useDifferentAddress, setUseDifferentAddress] = React.useState(false);
  const [selectedAddress, setSelectedAddress] = React.useState(null);
  const [shippingCharge, setShipingCharge] = useState(0);
  const [defaultAddress, setDefaultAddress] = useState({});
  const [pinAdress, setPinAdress] = useState(null);
  const [loading, setLoading] = React.useState(false); // Loading state for Pay Now button
  const { shippingChargesList } = useSelector((state) => state.shippingReducer);
  const { items } = useSelector((state) => state.buyNow);

  const { addressList } = useSelector((state) => state.address) ?? [];
  const { cartItems } = useSelector((state) => state.cart);
  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const [searchTerm, setSearchTerm] = useState("");
  const [shippingChargesId, setShippingChargesId] = useState(null);
  const [searchClickedTerm, setSearchClickedTerm] = useState("");
  const [addressFormInitialized, setAddressFormInitialized] =
    React.useState(false);
  const [shippingRemark, setShippingRemark] = useState("");
  const cart_amount = useSelector(
    (state) => state.auth?.user?.minimum_cart_amount
  );

  const [formData, setFormData] = React.useState({
    shipping_address_id: 0,
    billing_address_id: 0,
    cart_ids: [],
    payment_type: "Online",
    coupon_id: null,
    shipping_charges: 0,
    amount: 0,
    card_number: "",
    card_name: "",
    expiry: "",
    cvv: "",
    isCard: false, // add this flag to distinguish in UI
  });

  const [address, setAddress] = React.useState(addressForm);

  const [couponModalOpen, setCouponModalOpen] = React.useState(false);
  const [selectedCoupon, setSelectedCoupon] = React.useState(null);
  const [couponError, setCouponError] = React.useState("");

  // Get coupons from Redux
  const {
    coupons,
    loading: couponsLoading,
    error: couponsError,
  } = useSelector((state) => state.coupons);

  // // Fetch coupons when modal opens
  React.useEffect(() => {
    if (couponModalOpen) {
      dispatch(fetchCoupons());
    }
  }, [couponModalOpen]);

  // Fetch latest addresses on mount and whenever the address modal is used
  React.useEffect(() => {
    dispatch(getAddressList());
  }, [dispatch]);

  // When addressList changes, update formData and address fields
  React.useEffect(() => {
    // only sync on first mount _or_ right after a save
    if (!addressFormInitialized && addressList?.length > 0) {
      // first, try to find the address the user is working on:
      let active =
        addressList.find((addr) => addr.address_id === selectedAddress) ||
        addressList.find((addr) => addr.is_default);

        if (!active) {
          active = addressList[0];
        }
        
      dispatch(
        featchShippingCharges({
          user_type: userType,
          state: active?.state,
        })
      );

      setFormData((prev) => ({
        ...prev,
        billing_address_id: active.address_id,
        shipping_address_id: active.address_id,
      }));

      setAddress(
        addressForm.map((fieldDef) => ({
          ...fieldDef,
          enable: false,
          value: active[fieldDef.key] ?? "",
        }))
      );

      setSelectedAddress(active.address_id);

      // mark initialized so this effect won’t run again
      setAddressFormInitialized(true);
    }
  }, [addressList, addressFormInitialized, dispatch, userType]);

  // Add a ref to track the current default address ID
  const defaultAddressIdRef = React.useRef(null);

  // Modified useEffect to react to default address changes
  React.useEffect(() => {
    // Find the default address
    const defaultAddress = addressList?.find((addr) => addr.is_default);
    const defaultAddressId = defaultAddress?.address_id;

    // Check if we have a valid default address
    if (defaultAddress) {
      // Check if this is initial load or if default address has changed
      const isInitialLoad = !addressFormInitialized;
      const hasDefaultChanged =
        defaultAddressIdRef.current !== defaultAddressId;

      // Update our ref with the current default address ID
      defaultAddressIdRef.current = defaultAddressId;

      // Update form if this is initial load OR if default address has changed
      if (isInitialLoad || hasDefaultChanged) {
        // Set shipping charges based on the default address
        dispatch(
          featchShippingCharges({
            user_type: userType,
            state: defaultAddress?.state,
          })
        );

        // Update form data with the default address ID
        setFormData((prev) => ({
          ...prev,
          billing_address_id: defaultAddress.address_id,
          shipping_address_id: defaultAddress.address_id,
        }));

        // Update address form fields with values from default address
        setAddress(
          addressForm.map((fieldDef) => ({
            ...fieldDef,
            enable: false,
            value: defaultAddress[fieldDef.key] ?? "",
          }))
        );

        // Update selected address
        setSelectedAddress(defaultAddress.address_id);

        // Only set initialized to true on initial load
        if (isInitialLoad) {
          setAddressFormInitialized(true);
        }
      }
    } else if (addressList?.length > 0 && !addressFormInitialized) {
      // No default address, but we have addresses - use the first one
      const firstAddress = addressList[0];

      // Same logic as above but for first address
      dispatch(
        featchShippingCharges({
          user_type: userType,
          state: firstAddress?.state,
        })
      );

      setFormData((prev) => ({
        ...prev,
        billing_address_id: firstAddress.address_id,
        shipping_address_id: firstAddress.address_id,
      }));

      setAddress(
        addressForm.map((fieldDef) => ({
          ...fieldDef,
          enable: false,
          value: firstAddress[fieldDef.key] ?? "",
        }))
      );

      setSelectedAddress(firstAddress.address_id);
      setAddressFormInitialized(true);
    } else if (addressList?.length === 0 && !addressFormInitialized) {
      // No addresses at all
      toast.info("Please add a delivery address to continue");
      setAddressFormInitialized(true);
    }
  }, [addressList, addressFormInitialized, dispatch, userType]);

  // In the default address useEffect, add address deletion handling
  React.useEffect(() => {
    // First, check if selected address still exists when form is initialized
    if (addressFormInitialized && selectedAddress) {
      const selectedAddressExists = addressList?.some(
        (addr) => addr.address_id === selectedAddress
      );

      if (!selectedAddressExists) {
        // Reset selected address
        setSelectedAddress(null);

        // Find a new default address or first address
        const newDefaultAddress =
          addressList?.find((addr) => addr.is_default) || addressList?.[0];

        if (newDefaultAddress) {
          setSelectedAddress(newDefaultAddress.address_id);
          dispatch(
            featchShippingCharges({
              user_type: userType,
              state: newDefaultAddress?.state,
            })
          );

          // Update form data
          setFormData((prev) => ({
            ...prev,
            billing_address_id: newDefaultAddress.address_id,
            shipping_address_id: newDefaultAddress.address_id,
          }));

          // Update address fields
          setAddress(
            addressForm.map((fieldDef) => ({
              ...fieldDef,
              enable: false,
              value: newDefaultAddress[fieldDef.key] ?? "",
            }))
          );
        } else {
          // No addresses left
          AddressFieldEnable();
          setFormData((prev) => ({
            ...prev,
            billing_address_id: 0,
            shipping_address_id: 0,
          }));
        }
      }
    }

    // Continue with normal default address logic
    // const defaultAddress = addressList?.find(addr => addr.is_default);
    // const defaultAddressId = defaultAddress?.address_id;

    // // Check if we have a valid default address
    // if (defaultAddress) {
    //   // Check if this is initial load or if default address has changed
    //   const isInitialLoad = !addressFormInitialized;
    //   const hasDefaultChanged = defaultAddressIdRef.current !== defaultAddressId;

    //   // Update our ref with the current default address ID
    //   defaultAddressIdRef.current = defaultAddressId;

    //   // Update form if this is initial load OR if default address has changed
    //   if (isInitialLoad || hasDefaultChanged) {

    //     // Set shipping charges based on the default address
    //     dispatch(featchShippingCharges({
    //       user_type: userType,
    //       state: defaultAddress?.state
    //     }));

    //     // Update form data with the default address ID
    //     setFormData(prev => ({
    //       ...prev,
    //       billing_address_id: defaultAddress.address_id,
    //       shipping_address_id: defaultAddress.address_id,
    //     }));

    //     // Update address form fields with values from default address
    //     setAddress(addressForm.map(fieldDef => ({
    //       ...fieldDef,
    //       enable: false,
    //       value: defaultAddress[fieldDef.key] ?? '',
    //     })));

    //     // Update selected address
    //     setSelectedAddress(defaultAddress.address_id);

    //     // Only set initialized to true on initial load
    //     if (isInitialLoad) {
    //       setAddressFormInitialized(true);
    //     }
    //   }
    // } else if (addressList?.length > 0 && !addressFormInitialized) {
    //   // No default address, but we have addresses - use the first one
    //   const firstAddress = addressList[0];

    //   // Same logic as above but for first address
    //   dispatch(featchShippingCharges({
    //     user_type: userType,
    //     state: firstAddress?.state
    //   }));

    //   setFormData(prev => ({
    //     ...prev,
    //     billing_address_id: firstAddress.address_id,
    //     shipping_address_id: firstAddress.address_id,
    //   }));

    //   setAddress(addressForm.map(fieldDef => ({
    //     ...fieldDef,
    //     enable: false,
    //     value: firstAddress[fieldDef.key] ?? '',
    //   })));

    //   setSelectedAddress(firstAddress.address_id);
    //   setAddressFormInitialized(true);
    // } else if (addressList?.length === 0 && !addressFormInitialized) {
    //   // No addresses at all
    //   toast.info("Please add a delivery address to continue");
    //   setAddressFormInitialized(true);
    // }
  }, [
    addressList,
    addressFormInitialized,
    dispatch,
    userType,
    selectedAddress,
  ]);

  React.useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const cartIds = cartItems
        .filter((cart) => {
          if (userType === "vendor") {
            return (
              cart?.quantity * (cart?.sub_product?.no_of_pcs || 1) <=
              cart?.available_stock
            );
          } else {
            return cart?.quantity <= cart?.available_stock;
          }
        })
        .map((cart) => {
          return cart.cart_id;
        });

      setFormData((prev) => ({
        ...prev,
        cart_ids: cartIds,
      }));
    }
  }, [cartItems]);

  React.useEffect(() => {
    if (useDifferentAddress && selectedAddress === null && addressList.length) {
      setSelectedAddress(addressList[0].id);
    }
  }, [useDifferentAddress, selectedAddress, addressList]);

  const filteredCoupons = coupons.filter((coupon) => {
    const search = searchTerm.toLowerCase();
    return (
      coupon.coupon_code.toLowerCase().includes(search) ||
      (coupon.coup_title && coupon.coup_title.toLowerCase().includes(search)) ||
      (coupon.description && coupon.description.toLowerCase().includes(search))
    );
  });

  const handleSearchClick = () => {
    setSearchClickedTerm(searchTerm.trim());
  };

  const getAddressByPin = async (pincode) => {
    try {
      if (pincode?.length !== 6) {
        toast.info("Enter a 6-digit PIN code");

        // Reset relevant fields
        setAddress((prev) =>
          prev.map((field) =>
            ["city", "state", "country"].includes(field.key)
              ? { ...field, value: "" }
              : field
          )
        );

        setPinAdress({});
        return;
      }

      const response = await axios.get(
        `https://api.postalpincode.in/pincode/${pincode}`
      );

      if (response.status === 200 && response.data?.[0]?.Status === "Success") {
        const postOfficeData = response.data[0].PostOffice[0];

        setPinAdress(postOfficeData);

        // Update the address array state
        setAddress((prev) =>
          prev.map((field) => {
            if (field.key === "city") {
              return { ...field, value: postOfficeData.Block || "" };
            }
            if (field.key === "state") {
              return { ...field, value: postOfficeData.State || "" };
            }
            if (field.key === "country") {
              return { ...field, value: postOfficeData.Country || "" };
            }
            return field;
          })
        );

        return postOfficeData;
      } else {
        setAddress((prev) =>
          prev.map((field) =>
            ["city", "state", "country"].includes(field.key)
              ? { ...field, value: "" }
              : field
          )
        );

        setPinAdress({});
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const AddressFieldEnable = () => {
    const updatedAddress =
      addressForm.length > 0
        ? addressForm.map((item) => ({
            ...item,
            value: item?.key == "address_type" ? "WORK" : "",
            enable: true,
          }))
        : addressForm;

    setAddress(updatedAddress);
  };

  // 1) Total price of all items
  const productsTotal = items.reduce((sum, item) => {
    const isVendor = userType === "vendor";
    const subProduct = item?.sub_product || {};
    const price = isVendor
      ? subProduct.vendor_product_price
      : subProduct.customer_product_price || 0;
    const qty = isVendor
      ? (item?.quantity || 0) * (subProduct.no_of_pcs || 1)
      : item?.quantity || 0;
    const totalRequested = qty;
    const available = item?.available_stock || 0;

    // Only add to total if stock is sufficient
    if (totalRequested <= available) {
      return sum + price * qty;
    }

    return sum;
  }, 0);

  // 2) Your static (or dynamic) savings & shipping
  const shipping = 5;
  const couponDiscount = selectedCoupon
    ? selectedCoupon.offer_type == "percentage"
      ? (productsTotal * (selectedCoupon.discount || 0)) / 100
      : parseFloat(selectedCoupon.discount)
    : 0;

  // 3) Grand total
  const grandTotal = Math.max(productsTotal - couponDiscount, 0);

  React.useEffect(() => {
    if (shippingChargesList?.length > 0) {
      const {
        shipping_charges: temp = 0,
        shipping_remark: remark = "",
        id: shipping_charges_id,
      } = shippingChargesList.find(
        (item) => grandTotal >= item.min_price && grandTotal <= item.max_price
      ) || {};

      // now you have both in one shot:

      // set state/form however you like
      setShipingCharge(temp);
      setShippingChargesId(shipping_charges_id);
      setShippingRemark(remark);
      setFormData((prev) => ({
        ...prev,
        shipping_charges: temp,
      }));
    } else {
      setShipingCharge(0);
      setFormData((prev) => ({
        ...prev,
        shipping_charges: 0,
      }));
    }
  }, [shippingChargesList, grandTotal]);

  // Place order and handle payment
  const createOrder = async () => {
    if (cart_amount > grandTotal) {
      // toast.error(`Minimum cart amount is ₹${cart_amount}. Please add more items to your cart.`);
      Alert.Alert.ErrorAlert(
        `Minimum cart amount is ₹${cart_amount}. Please add more items to your cart.`
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        coupon_id: selectedCoupon ? selectedCoupon.coup_id : null, // send coup_id as coupon_id
        coupon_code: selectedCoupon ? selectedCoupon.coupon_code : null, // send coupon_code
        discount: couponDiscount ? couponDiscount : 0,
        amount: grandTotal + shippingCharge,
        shipping_charges_id: shippingChargesId,
      };

      const response = await dispatch(placeNewOrder(payload));

      if (response?.status === "success") {
        // UPI/Online
        if (formData?.payment_type === "Online") {
          const option = {
            key: response?.data?.key,
            id: response?.data?.razorpay_order_id,
            amount: grandTotal,
            user_info: {
              customer_name: response?.data?.user_info?.name,
              mobile_no: response?.data?.user_info?.mobile_no,
              email_id: response?.data?.user_info?.email_id,
            },
            // Do NOT pass method
          };
          try {
            const paymentResponse = await handlePayment(option);
            if (paymentResponse.status === "success") {
              const paymentVerificationResponse = await addPaymentResponse(
                paymentResponse?.paymentData
              );
              if (paymentVerificationResponse?.data?.status === "success") {
                toast.success(paymentVerificationResponse?.data?.message);
                await dispatch(fetchCart()); // <-- Sync cart with backend
                navigate(`/orders`);
              } else {
                toast.error(
                  paymentVerificationResponse?.data?.message ||
                    "Payment verification failed."
                );
                navigate(`/orders`);
              }
            } else {
              toast.error("Payment was not completed or failed.");
            }
          } catch (err) {
            toast.error("Payment failed. Please try again.");
          }
        }
        // Card
        else if (formData?.payment_type === "Card") {
          const option = {
            key: response.data.key,
            id: response.data.razorpay_order_id,
            amount: grandTotal,
            user_info: {
              customer_name: response.data.user_info.name,
              // Do NOT pass email or mobile_no
            },
            method: formData.isCard ? "card" : undefined,
          };

          const paymentResponse = await handlePayment(option);
          // ...then verify paymentResponse on your backend...
        }
        // COD
        else {
          if (response?.status === "success") {
            toast.success(response?.message);
            await dispatch(fetchCart()); // <-- Sync cart with backend
            navigate(`/orders`);
          } else {
            toast.error(response?.message || "Order placement failed.");
          }
        }
      } else {
        toast.error(response?.error || "Order placement failed.");
      }
    } catch (error) {
      toast.error("Order creation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Update formData with coupon and amount
  React.useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      amount: grandTotal,
      coupon_id: selectedCoupon ? selectedCoupon.coup_id : null, // <-- send coup_id as coupon_id
    }));
  }, [grandTotal, selectedCoupon]);

  const addAddress1 = async () => {
    // 1️⃣ Normalize all .value to strings so trim() is safe
    const normalized = address.map((item) => ({
      ...item,
      // if value is null/undefined/number/etc, coerce to '' or its string form
      value: item.value == null ? "" : String(item.value),
    }));

    // 2️⃣ Find which fields are blank after trimming
    const emptyFields = normalized
      .filter((item) => item.value.trim().length === 0)
      .map((item) => item.label || item.key);

    if (emptyFields.length > 0) {
      toast.info(
        `Please fill in the following field(s): ${emptyFields.join(", ")}`
      );
      return;
    }

    // 3️⃣ Build your payload from the normalized array
    const addForm = normalized.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});

    // 4️⃣ Any extra flags
    addForm.is_default = formData?.billing_address_id === 0;

    // 5️⃣ Dispatch, then directly update without triggering re-initialization
    try {
      const result = await dispatch(addAddress(addForm));

      if (result.success) {
        const newId = result.data?.address_id;
        if (newId) {
          setSelectedAddress(newId);

          // Just update the address list without re-initializing the form
          await dispatch(getAddressList());

          // Update form data with the new address ID
          setFormData((prev) => ({
            ...prev,
            billing_address_id: newId,
            shipping_address_id: newId,
          }));

          // DON'T reset addressFormInitialized to false!
          // Instead, keep it true to prevent re-initialization

          toast.success("Address saved successfully!");
        } else {
          toast.error("Address saved but no ID returned.");
        }
      } else {
        toast.error("Could not save address.");
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedAddress =
      address.length > 0
        ? address.map((item) => {
            if (item.key === name) {
              return {
                ...item,
                value: type === "checkbox" ? checked : value,
              };
            }
            return item;
          })
        : addressForm;

    setAddress(updatedAddress);
  };

  // Add this ref at the top of your component near other state declarations
  const emptyAddressToastShown = React.useRef(false);

  // Add this new useEffect that specifically monitors the address list
  React.useEffect(() => {
    // Check if address list is empty and toast hasn't been shown yet
    if (addressList?.length === 0 && !emptyAddressToastShown.current) {
      // toast.info("Please add a delivery address to continue");
      emptyAddressToastShown.current = true;
    } else if (addressList?.length > 0) {
      // Reset flag when addresses are added
      emptyAddressToastShown.current = false;
    }
  }, [addressList]);

  const handleRemoveItem = (itemToRemove) => {
    const updatedCartIds = formData.cart_ids.filter(
      (id) => id != itemToRemove.cart_id
    );

    setFormData((prev) => ({
      ...prev,
      cart_ids: updatedCartIds,
    }));
    // If using Redux:
    dispatch({ type: "REMOVE_BUY_NOW_ITEM", payload: itemToRemove });

    // If using local state:
    // setItems(prev => prev.filter(item => item.id !== itemToRemove.id));
  };

  const validateCheckout = () => {
    // Check billing and shipping address
    if (!formData.billing_address_id) {
      toast.error("Please add a billing address in Profile.");
      return false;
    }
    if (!formData.shipping_address_id) {
      toast.error("Please select a shipping address in Profile.");
      return false;
    }
    // Check payment type
    if (!formData.payment_type) {
      toast.error("Please select a payment method.");
      return false;
    }
    // If payment type is Card, check card details
    if (formData.payment_type === "Card") {
      if (
        !formData.card_number ||
        !formData.card_name ||
        !formData.expiry ||
        !formData.cvv
      ) {
        toast.error("Please fill all card details.");
        return false;
      }
    }

    // Check cart items
    if (!formData.cart_ids || formData.cart_ids.length === 0) {
      toast.error("Your cart is empty.");
      return false;
    }
    return true;
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-lg text-gray-500 mb-4">
          No items selected for checkout.
        </p>
        <Link
          to="/cart"
          className="text-purple-600 hover:underline font-medium"
        >
          Go back to Cart
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 flex flex-col lg:flex-row gap-8 font-sans">
      {/* Left: Billing & Shipping Section */}
      <div className="flex-1 space-y-6">
        <h2 className="text-xl font-semibold border-l-4 border-teal-600 pl-3">
          Review Order
        </h2>

        {/* Billing Details */}
        <div className="space-y-4">
          <div className="flex flex-row justify-content-between w-full ">
            <h3 className="text-lg font-medium">Billing Details</h3>
            <button
              className="ml-2 px-4 py-2 bg-sky-200 text-sky-800 rounded-md shadow-sm hover:bg-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm transition-all duration-200"
              title="Add a new address"
              onClick={() => {
                AddressFieldEnable();
              }}
            >
              Add New Address
            </button>
          </div>

          <div className="flex flex-wrap gap-4">
            {address.length > 0 &&
              address.map(({ label, key, value, enable }, i) => (
                <div key={i} className="flex flex-col w-full sm:w-[48%]">
                  <label className="text-sm font-medium mb-1">{label}</label>
                  {key !== "address_type" ? (
                    <input
                      type="text"
                      name={key}
                      maxLength={key === "mobile_number" ? 10 : undefined}
                      defaultValue={value}
                      value={value}
                      className={`border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
                        !addressList?.length ||
                        !addressList?.some((addr) => addr.is_default)
                          ? "border-yellow-300 focus:ring-yellow-500 bg-yellow-50"
                          : "border-gray-300 focus:ring-purple-500"
                      }`}
                      disabled={
                        addressList?.length > 0 ||
                        addressList?.some((addr) => addr.is_default)
                          ? !enable
                          : false
                      }
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        if (key === "pin_code") {
                          getAddressByPin(e.target.value);
                        }
                      }}
                    />
                  ) : (
                    // Address Type select dropdown
                    <select
                      name={key}
                      value={value}
                      disabled={
                        addressList?.length > 0 ||
                        addressList?.some((addr) => addr.is_default)
                          ? !enable
                          : false
                      }
                      onChange={handleInputChange}
                      className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                        !addressList?.length ||
                        !addressList?.some((addr) => addr.is_default)
                          ? "border-yellow-300 focus:ring-yellow-500 bg-yellow-50"
                          : "border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 bg-white"
                      }`}
                    >
                      <option value="HOME">Home</option>
                      <option value="WORK">Work</option>
                    </select>
                  )}
                </div>
              ))}
            {/* Button aligned to the right */}
            {
              <div className="w-full flex justify-end">
                <button
                  className="px-4 py-2 bg-sky-200 text-sky-800 rounded-md shadow-sm hover:bg-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm transition-all duration-200"
                  title="Add a new address"
                  onClick={() => {
                    addAddress1();
                  }}
                >
                  Save Address
                </button>
              </div>
            }
          </div>
        </div>

        {/* Shipping Address */}
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Shipping Address</h3>
          <div className="border rounded p-4 space-y-2">
            <label className="block">
              <input
                type="radio"
                name="shipping"
                className="mr-2"
                checked={!useDifferentAddress}
                onChange={() => {
                  setFormData((prev) => ({
                    ...prev,
                    shipping_address_id: prev?.billing_address_id,
                  }));
                  setUseDifferentAddress(false);

                  // Set Billing Details to default address
                  const defaultAddress = addressList.find(
                    (addr) => addr.is_default
                  );
                  if (defaultAddress) {
                    setAddress((prev) =>
                      prev.map((item) => ({
                        ...item,
                        value: defaultAddress[item.key] || "",
                      }))
                    );
                    dispatch(
                      featchShippingCharges({
                        user_type: userType,
                        state: defaultAddress?.state,
                      })
                    );
                    setSelectedAddress(defaultAddress.address_id);
                  }
                }}
              />
              Same as Billing address
            </label>
            <label className="block">
              <input
                type="radio"
                name="shipping"
                className="mr-2"
                checked={useDifferentAddress}
                onChange={() => setUseDifferentAddress(true)}
              />
              Use a different shipping address
            </label>
          </div>

          {useDifferentAddress && (
            <Disclosure
              as="div"
              className="mt-4 border rounded overflow-hidden"
            >
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full px-4 py-2 flex justify-between items-center bg-gray-100 text-sm font-semibold hover:bg-gray-200">
                    <span>Select an Address</span>
                    <ChevronUpIcon
                      className={`w-5 h-5 transform transition ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="p-4 bg-white space-y-3">
                    {addressList.map((item) => (
                      <label
                        key={item.address_id}
                        className={`block p-3 border rounded cursor-pointer ${
                          selectedAddress === item.id
                            ? "border-purple-600 bg-purple-50"
                            : "hover:border-purple-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="customAddress"
                          className="mr-2"
                          checked={selectedAddress === item.address_id}
                          onChange={() => {
                            setFormData((prev) => ({
                              ...prev,
                              shipping_address_id: item.address_id,
                            }));
                            setSelectedAddress(item.address_id);
                            dispatch(
                              featchShippingCharges({
                                user_type: userType,
                                state: item?.state,
                              })
                            );

                            // Update Billing Details with selected shipping address
                            //  setAddress(prev =>
                            //  prev.map(field => ({
                            //    ...field,
                            //    value: item[field.key] || '',
                            //  }))
                            //);
                          }}
                        />
                        <span className="font-medium">{item.address_type}</span>
                        <div className="text-sm text-gray-600 mt-1">
                          {item.house_no}, {item.street}, {item?.landmark},{" "}
                          {item?.city}-{item?.pin_code}, {item?.state},{" "}
                          {item?.country}
                        </div>
                      </label>
                    ))}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          )}
        </div>

        {/* Payment Method */}
        <div>
          <h3 className="text-lg font-medium mt-6 mb-2">Payment Method</h3>
          <p className="text-sm text-gray-600 mb-2">
            All transactions are secure and encrypted.
          </p>

          <div className="space-y-4 border rounded p-4">
            {/* Online Payment */}
            <label className="block">
              <input
                type="radio"
                name="payment"
                value="Online"
                className="mr-2"
                checked={formData.payment_type === "Online"}
                onChange={() =>
                  setFormData((prev) => ({ ...prev, payment_type: "Online" }))
                }
              />
              Online Payment (UPI / Card / Netbanking)
              <p className="text-sm text-gray-500 ml-5">
                Pay securely using UPI, Credit/Debit Card, or Netbanking.
              </p>
            </label>

            {/* Cash on Delivery */}
            <label className="block">
              <input
                type="radio"
                name="payment"
                value="COD"
                className="mr-2"
                checked={formData.payment_type === "COD"}
                onChange={() =>
                  setFormData((prev) => ({ ...prev, payment_type: "COD" }))
                }
              />
              Cash on delivery
              <p className="text-sm text-gray-500 ml-5">
                Pay with cash upon delivery.
              </p>
            </label>
          </div>
        </div>

        {/* “Pay Now” / “Place Order” Button */}
        <button
          onClick={() => {
            if (!loading && validateCheckout()) createOrder();
          }}
          disabled={loading || formData?.billing_address_id === 0}
          className={`bg-purple-800 text-white px-6 py-3 rounded mt-6 hover:bg-purple-700 flex items-center justify-center ${
            loading || formData?.billing_address_id === 0
              ? "opacity-60 cursor-not-allowed"
              : ""
          }`}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
              Processing...
            </>
          ) : formData.payment_type === "COD" ? (
            "Place Order"
          ) : (
            "Pay Now"
          )}
        </button>
      </div>

      {/* Right: Order Summary */}
      <div className=" flex flex-col w-full lg:w-[40%] border rounded-lg p-4 space-y-6 shadow-md self-start h-[83vh]">
        <h3 className="text-lg font-semibold">Order Summary</h3>

        {/* — Iterate over products inside the same card — */}
        <div className="flex-1 mt-4 space-y-6 overflow-y-auto ">
          {items.map((item, idx) => {
            const product = item?.product || {};
            const sub = item?.sub_product || {};
            const qty = item?.quantity || 0;
            let stock = item?.available_stock;
            let peaceWiseQuantity =
              userType === "vendor" ? qty * (sub?.no_of_pcs || 1) : qty;
            const unitPrice =
              userType === "vendor"
                ? sub.vendor_product_price || 0
                : sub.customer_product_price || 0;
            const lineTotal = unitPrice * peaceWiseQuantity;
            if (peaceWiseQuantity <= stock) {
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between space-x-4"
                >
                  <img
                    src={
                      sub.sub_pro_img ||
                      product.product_primary_img ||
                      "/placeholder.jpg"
                    }
                    alt={product.product_name}
                    className="w-24 h-20 object-cover rounded"
                  />
                  <div>
                    <p className="text-sm font-medium">
                      {product.product_name}
                    </p>
                    <p className="text-xs text-gray-500">Color: {sub.color}</p>
                    <p className="text-xs text-gray-500">Size: {sub.size}</p>
                    {userType === "vendor" && sub.selling_type != "Pieces" ? (
                      <p className="text-xs text-red-600">
                        {qty} Set = {sub.no_of_pcs * qty} pieces
                      </p>
                    ) : (
                      ""
                    )}
                  </div>
                  <span className="ml-auto font-medium">
                    ₹{lineTotal.toFixed(2)}
                  </span>
                  <button
                    className="ml-2 text-red-600 hover:text-red-800 text-lg"
                    title="Remove from order"
                    onClick={() => handleRemoveItem(item)}
                  >
                    &times;
                  </button>
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>

        {/* Coupon button and info */}
        <div className="mb-2">
          <button
            type="button"
            className="bg-[#bb7070] hover:bg-[#a65c5c] text-white px-4 py-2 rounded w-full"
            onClick={() => setCouponModalOpen(true)}
          >
            Apply Coupons
          </button>
          {selectedCoupon && (
            <div className="text-green-600 text-xs mt-2">
              Coupon <b>{selectedCoupon.code}</b> applied:{" "}
              {selectedCoupon.description}
              <button
                className="ml-2 text-red-500 underline text-xs"
                onClick={() => setSelectedCoupon(null)}
              >
                Remove
              </button>
            </div>
          )}
          {couponError && (
            <div className="text-red-500 text-xs mt-1">{couponError}</div>
          )}
        </div>

        {/* — Totals section — */}
        <div className="border-t pt-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>₹ {productsTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping Charges</span>
            <span className="text-red-600">+ ₹ {shippingCharge}</span>
          </div>
          {shippingRemark && (
            <div className="flex justify-between text-xs text-gray-500">
              <span>Shipping Remark</span>
              <span className="italic">{shippingRemark}</span>
            </div>
          )}
          {/* <div className="flex justify-between">
            <span>Coupon</span>
            <span className="text-green-600">- ₹{couponDiscount.toFixed(2)}</span>
          </div> */}
          {selectedCoupon && (
            <div className="flex justify-between">
              <span>Coupon</span>
              <span className="text-green-600">
                - ₹ {couponDiscount.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-base border-t pt-2">
            <span>Total</span>
            <span>₹ {(grandTotal + shippingCharge).toFixed(2)}</span>
          </div>
          <div className="flex justify-end font-semibold text-base border-t pt-2">
            <p style={{ color: "red", fontSize: 12 }}>
              Inclusive of all taxes.{" "}
            </p>
          </div>
        </div>
      </div>

      {/* Coupon Modal */}
      <other.Modal
        isOpen={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        title="Available Coupons"
      >
        {/* <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search coupons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearchClick();
            }}
            className="w-full pl-10 p-2 border rounded text-sm focus:ring focus:ring-purple-200"
          />
          <button
            onClick={handleSearchClick}
            type="button"
            className="absolute inset-y-0 right-5 pl-3 flex items-center text-gray-500 hover:text-gray-700"
          >
            <Search size={16} />
          </button>
        </div> */}

        {couponsLoading && <div>Loading coupons...</div>}
        {couponsError && <div className="text-red-500">{couponsError}</div>}
        {filteredCoupons.length === 0 && !couponsLoading ? (
          <div className="text-center text-gray-500 py-8">
            No coupons available at the moment.
          </div>
        ) : (
          <ul>
            {filteredCoupons.map((coupon) => {
              let bg =
                selectedCoupon?.coup_id == coupon.coup_id ||
                productsTotal < (coupon.min_amt || 0)
                  ? "bg-[#a65c5c]"
                  : "bg-[#bb7070]";
              return (
                <li
                  key={coupon.coup_id}
                  className="mb-3 p-3 border rounded hover:bg-purple-50 flex sm:flex-row sm:items-center sm:justify-between gap-2"
                >
                  <div className="flex-1">
                    <div className="font-semibold">{coupon.coupon_code}</div>
                    <div className="text-xs text-gray-600">
                      {coupon.coup_title || coupon.description}
                    </div>
                    <div className="text-xs text-gray-500">
                      Discount: {coupon.discount}{" "}
                      {coupon.offer_type == "percentage" ? "%" : "₹"}
                    </div>
                    {productsTotal < (coupon.min_amt || 0) && (
                      <div className="text-red-500 text-xs mt-1">
                        Not eligible: Minimum order ₹{coupon.min_amt}
                      </div>
                    )}
                  </div>
                  <button
                    className={` ${bg} hover:bg-[#a65c5c] text-white px-4 py-2 rounded text-base self-start sm:self-center`}
                    onClick={() => {
                      if (selectedCoupon?.coup_id != coupon.coup_id) {
                        if (productsTotal >= (coupon.min_amt || 0)) {
                          setSelectedCoupon(coupon);
                          setCouponError("");
                          setCouponModalOpen(false);
                          toast.success(
                            `Coupon ${coupon.coupon_code} applied!`
                          );
                        } else {
                          setCouponError(
                            `Order must be at least ₹${coupon.min_amt} to use this coupon.`
                          );
                          toast.error(
                            `Order must be at least ₹${coupon.min_amt} to use this coupon.`
                          );
                        }
                      }
                    }}
                    disabled={productsTotal < (coupon.min_amt || 0)}
                  >
                    {selectedCoupon?.coup_id == coupon.coup_id
                      ? "Applied"
                      : productsTotal < (coupon.min_amt || 0)
                      ? "Not Eligible"
                      : "Apply"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </other.Modal>
    </div>
  );
};

export default Checkout;
